import React from "react";
import { Link } from "react-router-dom";
import { Swiper, SwiperSlide } from "swiper/react";
import { Autoplay, Pagination, EffectCreative } from "swiper/modules";
import { StoreSection } from "../../../../redux/services/store/storePagesApi";

import "swiper/css";
import "swiper/css/pagination";
import "swiper/css/effect-creative";

type SectionItem = {
  id: number | string;
  slot?: string | null;
  sort_order?: number;
  is_active?: boolean;
  content_json?: any;
};

type SectionWithItems = StoreSection & {
  settings_json?: any;
  items?: SectionItem[];
};

type Props = {
  section?: SectionWithItems;
};

type SlideItem = {
  id: number | string;
  title: string;
  subtitle: string;
  buttonText: string;
  buttonLink: string;
  image: string;
  imagePosition?: string;
};

const fallbackSlides: SlideItem[] = [
  {
    id: 1,
    title: "Spring Fashion Edit",
    subtitle: "Minimal silhouettes, soft tones, and statement textures.",
    buttonText: "Shop Now",
    buttonLink: "/shop",
    image:
      "https://images.unsplash.com/photo-1529139574466-a303027c1d8b?auto=format&fit=crop&w=1400&q=80",
    imagePosition: "78% 12%",
  },
  {
    id: 2,
    title: "Everyday Luxe",
    subtitle: "Clean wardrobe essentials designed for modern dressing.",
    buttonText: "Explore",
    buttonLink: "/shop",
    image:
      "https://images.unsplash.com/photo-1483985988355-763728e1935b?auto=format&fit=crop&w=1400&q=80",
    imagePosition: "76% 10%",
  },
];

const getSlides = (section?: SectionWithItems): SlideItem[] => {
  const items = [...(section?.items || [])]
    .filter((item) => item && item.content_json && item.is_active !== false)
    .sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0));

  if (!items.length) return fallbackSlides;

  return items.map((item, index) => {
    const fb = fallbackSlides[index % fallbackSlides.length];
    return {
      id: item.id,
      title: item.content_json?.title || fb.title,
      subtitle: item.content_json?.subtitle || fb.subtitle,
      buttonText: item.content_json?.button_text || fb.buttonText,
      buttonLink: item.content_json?.button_link || fb.buttonLink,
      image: item.content_json?.image_url || item.content_json?.image || fb.image,
      imagePosition: item.content_json?.image_position || fb.imagePosition,
    };
  });
};

const FashionHeroNotebookSliderSection: React.FC<Props> = ({ section }) => {
  const slides = getSlides(section);
  const settings = section?.settings_json || {};

  const sectionBg = settings?.background_color || "#f7f4ef";
  const badgeText = settings?.badge_text || "Fashion Story";

  return (
    <section
      className="fashion-notebook-slider-demo py-5"
      style={{ background: sectionBg }}
    >
      <div className="container">
        <Swiper
          modules={[Autoplay, Pagination, EffectCreative]}
          slidesPerView={1}
          loop={slides.length > 1}
          speed={900}
          autoplay={
            slides.length > 1
              ? {
                  delay: 3200,
                  disableOnInteraction: false,
                }
              : false
          }
          pagination={{
            clickable: true,
          }}
          effect="creative"
          creativeEffect={{
            prev: {
              translate: ["-12%", 0, -1],
              scale: 0.96,
              opacity: 0.4,
            },
            next: {
              translate: ["12%", 0, -1],
              scale: 0.96,
              opacity: 0.4,
            },
          }}
          className="fashion-notebook-swiper"
        >
          {slides.map((slide) => (
            <SwiperSlide key={slide.id}>
              <div className="px-md-3 px-lg-4">
                <div className="fashion-notebook-frame position-relative rounded-4 overflow-hidden">
                  <div className="row g-0 align-items-stretch">
                    <div className="col-12 col-lg-5">
                      <div className="fashion-notebook-content h-100 d-flex align-items-center">
                        <div className="p-4 p-md-5 p-lg-5">
                          <span
                            className="d-inline-block mb-3 rounded-pill px-3 py-2"
                            style={{
                              background: "#efe7dc",
                              color: "#6f6255",
                              fontSize: 12,
                              fontWeight: 600,
                              letterSpacing: "1px",
                            }}
                          >
                            {badgeText}
                          </span>

                          <h3
                            className="mb-3"
                            style={{
                              fontSize: "clamp(32px, 4vw, 56px)",
                              lineHeight: 1.04,
                              fontWeight: 700,
                              color: "#111111",
                              maxWidth: 420,
                            }}
                          >
                            {slide.title}
                          </h3>

                          <p
                            className="mb-4"
                            style={{
                              fontSize: 15,
                              lineHeight: 1.8,
                              maxWidth: 360,
                              color: "#666666",
                            }}
                          >
                            {slide.subtitle}
                          </p>

                          <Link
                            to={slide.buttonLink}
                            className="btn rounded-pill px-4 py-2"
                            style={{
                              background: "#111111",
                              color: "#ffffff",
                              border: "none",
                              fontWeight: 700,
                              minWidth: 140,
                            }}
                          >
                            {slide.buttonText}
                          </Link>
                        </div>
                      </div>
                    </div>

                    <div className="col-12 col-lg-7">
                      <div
                        className="fashion-notebook-image position-relative h-100"
                        style={{
                          backgroundImage: `url(${slide.image})`,
                          backgroundSize: "cover",
                          backgroundPosition: slide.imagePosition || "78% 10%",
                        }}
                      >
                        <div
                          className="position-absolute top-0 start-0 w-100 h-100"
                          style={{
                            background:
                              "linear-gradient(90deg, rgba(247,244,239,0.06) 0%, rgba(247,244,239,0.00) 26%, rgba(0,0,0,0.05) 100%)",
                          }}
                        />
                      </div>
                    </div>
                  </div>

                  <div
                    className="fashion-notebook-ring d-none d-lg-flex flex-column"
                    aria-hidden="true"
                  >
                    <span />
                    <span />
                    <span />
                    <span />
                    <span />
                    <span />
                  </div>
                </div>
              </div>
            </SwiperSlide>
          ))}
        </Swiper>
      </div>

      <style>
        {`
          .fashion-notebook-swiper {
            padding-bottom: 46px;
          }

          .fashion-notebook-frame {
            background: #fffdf9;
            box-shadow: 0 18px 50px rgba(0, 0, 0, 0.10);
            transition: transform 0.35s ease, box-shadow 0.35s ease;
          }

          .fashion-notebook-frame:hover {
            transform: translateY(-4px);
            box-shadow: 0 24px 60px rgba(0, 0, 0, 0.14);
          }

          .fashion-notebook-content {
            min-height: 480px;
            background: #fffdf9;
          }

          .fashion-notebook-image {
            min-height: 480px;
          }

          .fashion-notebook-ring {
            position: absolute;
            top: 50%;
            left: calc(41.666667% - 10px);
            transform: translateY(-50%);
            gap: 14px;
            z-index: 4;
          }

          .fashion-notebook-ring span {
            width: 16px;
            height: 16px;
            border-radius: 999px;
            border: 2px solid rgba(120, 109, 95, 0.45);
            background: #f7f4ef;
            box-shadow: 0 1px 3px rgba(0,0,0,0.08);
          }

          .fashion-notebook-slider-demo .swiper-pagination-bullet {
            width: 10px;
            height: 10px;
            opacity: 1;
            background: rgba(17, 17, 17, 0.18);
          }

          .fashion-notebook-slider-demo .swiper-pagination-bullet-active {
            background: #111111;
          }

          @media (max-width: 991.98px) {
            .fashion-notebook-content {
              min-height: auto;
            }

            .fashion-notebook-image {
              min-height: 420px;
              background-position: center top !important;
            }
          }

          @media (max-width: 575.98px) {
            .fashion-notebook-image {
              min-height: 340px;
            }
          }
        `}
      </style>
    </section>
  );
};

export default FashionHeroNotebookSliderSection;